import { useEffect, useState } from 'react';
import type { Auslastung, BelegungEintrag, HalbtagBelegung } from '@arbeitsrapporte/shared';
import { api } from '../api';
import { Fehler } from '../bausteine/Meldung';
import { datumLesbar, heute } from '../format';

/**
 * Wer wann wo ist – für die Disposition.
 *
 * Oben das Raster: je Mitarbeiter und Werktag zwei Felder, Vormittag und
 * Nachmittag. Darunter die Liste, nach der die Disposition eigentlich
 * fragt: Wer hat wann einen halben Tag oder mehr nichts vor?
 *
 * **Die Oberfläche rechnet nichts nach.** Was ein halber Tag ist und ob er
 * belegt ist, sagt `GET /api/auslastung`; hier wird nur gezeichnet und
 * sortiert.
 */

const ZEITRAEUME = ['diese', 'naechste', 'vier'] as const;
type Zeitraum = (typeof ZEITRAEUME)[number];

const ZEITRAUM_TEXT: Record<Zeitraum, string> = {
  diese: 'Diese Woche',
  naechste: 'Nächste Woche',
  vier: '4 Wochen',
};

/**
 * Die Töne des Hauses: bernstein heißt „hier ist etwas zu tun" – ein freier
 * halber Tag ist genau das für die Disposition.
 */
const TON: Record<HalbtagBelegung, string> = {
  frei: 'bg-amber-200',
  einsatz: 'bg-sky-500',
  termin: 'bg-slate-400',
  abwesend: 'bg-rose-200',
};

const BELEGUNG_TEXT: Record<HalbtagBelegung, string> = {
  frei: 'frei',
  einsatz: 'Einsatz',
  termin: 'Termin',
  abwesend: 'abwesend',
};

const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

function tagPlus(iso: string, tage: number): string {
  const d = new Date(`${iso}T00:00:00Z`);

  d.setUTCDate(d.getUTCDate() + tage);

  return d.toISOString().slice(0, 10);
}

function montagDieserWoche(): string {
  const tag = heute();
  const wochentag = new Date(`${tag}T00:00:00Z`).getUTCDay();

  return tagPlus(tag, -((wochentag + 6) % 7));
}

function zeitraumAus(z: Zeitraum): { von: string; bis: string } {
  const montag = montagDieserWoche();

  if (z === 'naechste') return { von: tagPlus(montag, 7), bis: tagPlus(montag, 13) };
  if (z === 'vier') return { von: montag, bis: tagPlus(montag, 27) };

  return { von: montag, bis: tagPlus(montag, 6) };
}

function tagKurz(iso: string): string {
  return `${WOCHENTAG[new Date(`${iso}T00:00:00Z`).getUTCDay()]} ${datumLesbar(iso).slice(0, 6)}`;
}

function eintragText(e: BelegungEintrag): string {
  const zeit = e.ganztags ? 'ganztägig' : `${e.von ?? '…'}–${e.bis ?? '…'}`;
  const was = e.art === 'einsatz' ? `Einsatz: ${e.titel}` : e.titel || '(ohne Betreff)';

  return [zeit, was, e.ort].filter(Boolean).join(' · ');
}

function prozent(verplant: number, verfuegbar: number): number | null {
  return verfuegbar === 0 ? null : Math.round((verplant / verfuegbar) * 100);
}

type Auswahl = { mitarbeiterId: string; datum: string };

function Raster({
  daten,
  auswahl,
  waehle,
}: {
  daten: Auslastung;
  auswahl: Auswahl | null;
  waehle: (a: Auswahl) => void;
}) {
  const tage = daten.mitarbeiter[0]?.tage.map((t) => t.datum) ?? [];

  return (
    <div className="overflow-x-auto rounded border border-slate-200 bg-white">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-600">
            <th className="sticky left-0 bg-white px-3 py-2 font-medium">Mitarbeiter</th>
            <th className="px-2 py-2 text-right font-medium">Auslastung</th>
            {tage.map((t) => (
              <th
                key={t}
                className={`px-1 py-2 text-center font-medium whitespace-nowrap ${
                  t === heute() ? 'text-slate-900 underline underline-offset-4' : ''
                }`}
              >
                {tagKurz(t)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {daten.mitarbeiter.map((m) => {
            const anteil = prozent(m.verplant, m.verfuegbar);

            return (
              <tr key={m.id} className="border-b border-slate-100 last:border-0">
                <th
                  scope="row"
                  className="sticky left-0 bg-white px-3 py-2 text-left font-normal whitespace-nowrap"
                >
                  {m.name}
                  {m.kalenderFehler && (
                    <span
                      title={m.kalenderFehler}
                      className="ml-2 rounded border border-amber-300 bg-amber-50 px-1 text-xs text-amber-800"
                    >
                      ohne Kalender
                    </span>
                  )}
                </th>
                <td className="px-2 py-2 text-right whitespace-nowrap text-slate-700 tabular-nums">
                  {anteil === null ? '–' : `${anteil} %`}
                </td>
                {m.tage.map((t) => {
                  const gewaehlt = auswahl?.mitarbeiterId === m.id && auswahl.datum === t.datum;

                  return (
                    <td key={t.datum} className="px-1 py-1">
                      <button
                        type="button"
                        onClick={() => waehle({ mitarbeiterId: m.id, datum: t.datum })}
                        title={[
                          `${m.name}, ${tagKurz(t.datum)}`,
                          `Vormittag: ${BELEGUNG_TEXT[t.vormittag]}`,
                          `Nachmittag: ${BELEGUNG_TEXT[t.nachmittag]}`,
                          ...t.eintraege.map(eintragText),
                        ].join('\n')}
                        aria-label={`${m.name}, ${tagKurz(t.datum)}: Vormittag ${BELEGUNG_TEXT[t.vormittag]}, Nachmittag ${BELEGUNG_TEXT[t.nachmittag]}`}
                        className={`flex min-h-0 w-full min-w-10 flex-col gap-0.5 rounded p-0.5 ${
                          gewaehlt ? 'ring-2 ring-slate-900' : 'hover:ring-1 hover:ring-slate-400'
                        }`}
                      >
                        <span className={`block h-4 rounded-sm ${TON[t.vormittag]}`} />
                        <span className={`block h-4 rounded-sm ${TON[t.nachmittag]}`} />
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Legende({ daten }: { daten: Auslastung }) {
  const { vormittag, nachmittag } = daten.halbtage;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
      {(Object.keys(TON) as HalbtagBelegung[]).map((b) => (
        <span key={b} className="flex items-center gap-1">
          <span className={`inline-block h-3 w-3 rounded-sm ${TON[b]}`} />
          {BELEGUNG_TEXT[b]}
        </span>
      ))}
      <span>
        Oben Vormittag ({vormittag.von}–{vormittag.bis}), unten Nachmittag ({nachmittag.von}–
        {nachmittag.bis}).
      </span>
    </div>
  );
}

function Tagesdetail({ daten, auswahl }: { daten: Auslastung; auswahl: Auswahl }) {
  const person = daten.mitarbeiter.find((m) => m.id === auswahl.mitarbeiterId);
  const tag = person?.tage.find((t) => t.datum === auswahl.datum);

  if (!person || !tag) return null;

  return (
    <section className="mt-4 rounded border border-slate-200 bg-white p-4">
      <h2 className="font-medium">
        {person.name} – {tagKurz(tag.datum)}
      </h2>
      <p className="mb-2 text-sm text-slate-600">
        Vormittag {BELEGUNG_TEXT[tag.vormittag]}, Nachmittag {BELEGUNG_TEXT[tag.nachmittag]}
      </p>
      {person.kalenderFehler && (
        <p className="mb-2 text-sm text-amber-800">
          Kalender nicht gelesen: {person.kalenderFehler}
        </p>
      )}
      {tag.eintraege.length === 0 ? (
        <p className="text-sm text-slate-600">Nichts eingetragen.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {tag.eintraege.map((e, i) => (
            <li key={i} className="flex gap-2">
              <span
                className={`mt-1 inline-block h-3 w-3 shrink-0 rounded-sm ${
                  e.art === 'einsatz'
                    ? TON.einsatz
                    : e.anzeige === 'abwesend'
                      ? TON.abwesend
                      : TON.termin
                }`}
              />
              <span>
                {e.art === 'einsatz' && e.einsatzId ? (
                  <a href="/einsaetze" className="underline underline-offset-4">
                    {eintragText(e)}
                  </a>
                ) : (
                  eintragText(e)
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Wer wann einen halben Tag oder mehr frei hat – ab heute.
 *
 * Nach Tagen, denn so wird gefragt: „Wen kann ich am Donnerstag schicken?"
 */
function Leerlauf({ daten }: { daten: Auslastung }) {
  const ab = heute();
  const tage = new Map<string, { name: string; wann: string; ohneKalender: boolean }[]>();

  for (const m of daten.mitarbeiter) {
    for (const t of m.tage) {
      if (t.datum < ab) continue;

      const vm = t.vormittag === 'frei';
      const nm = t.nachmittag === 'frei';

      if (!vm && !nm) continue;

      const liste = tage.get(t.datum) ?? [];

      liste.push({
        name: m.name,
        wann: vm && nm ? 'ganzer Tag' : vm ? 'Vormittag' : 'Nachmittag',
        ohneKalender: Boolean(m.kalenderFehler),
      });
      tage.set(t.datum, liste);
    }
  }

  return (
    <section className="mt-8">
      <h2 className="mb-1 text-lg font-semibold">Leerlauf</h2>
      <p className="mb-3 text-sm text-slate-600">
        Wer ab heute einen halben Tag oder mehr weder einen Einsatz noch einen Termin hat.
      </p>

      {tage.size === 0 ? (
        <p className="text-slate-600">Niemand hat in diesem Zeitraum Leerlauf.</p>
      ) : (
        <ul className="space-y-2">
          {[...tage.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([datum, wer]) => (
              <li key={datum} className="rounded border border-slate-200 bg-white p-3">
                <div className="mb-1 font-medium">{tagKurz(datum)}</div>
                <ul className="flex flex-wrap gap-2 text-sm">
                  {wer.map((w) => (
                    <li
                      key={w.name}
                      className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-900"
                    >
                      {w.name} · {w.wann}
                      {w.ohneKalender && (
                        <span className="text-amber-700"> (Kalender nicht gelesen)</span>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
        </ul>
      )}
    </section>
  );
}

export function AuslastungSeite() {
  const [zeitraum, setZeitraum] = useState<Zeitraum>('diese');
  const [daten, setDaten] = useState<Auslastung | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [auswahl, setAuswahl] = useState<Auswahl | null>(null);

  useEffect(() => {
    const { von, bis } = zeitraumAus(zeitraum);

    setDaten(null);
    setFehler(null);
    setAuswahl(null);
    api.auslastung(von, bis).then(setDaten).catch(setFehler);
  }, [zeitraum]);

  /* Derselbe Grund bei allen – meist fehlt die Einrichtung – ist ein Satz, nicht zehn. */
  const kalenderFehler = [
    ...new Set(daten?.mitarbeiter.map((m) => m.kalenderFehler).filter(Boolean) ?? []),
  ];

  return (
    <>
      <h1 className="mb-1 text-2xl font-semibold">Auslastung</h1>
      <p className="mb-4 text-slate-600">
        Einsätze und Outlook-Kalender zusammen, in halben Tagen. Ein Feld antippen zeigt, was an dem
        Tag ansteht.
      </p>

      <div className="mb-4 flex flex-wrap gap-2">
        {ZEITRAEUME.map((z) => (
          <button
            key={z}
            type="button"
            aria-pressed={zeitraum === z}
            onClick={() => setZeitraum(z)}
            className={`min-h-0 rounded-full border px-3 py-1.5 text-sm font-medium ${
              zeitraum === z
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
            }`}
          >
            {ZEITRAUM_TEXT[z]}
          </button>
        ))}
      </div>

      <Fehler fehler={fehler} />

      {daten === null && !fehler && <p className="text-slate-600">Lädt …</p>}

      {kalenderFehler.length > 0 && (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          <p className="font-medium">
            Nicht jeder Kalender ließ sich lesen – dort zählen nur die Einsätze.
          </p>
          {kalenderFehler.map((f) => (
            <p key={f}>{f}</p>
          ))}
        </div>
      )}

      {daten && daten.mitarbeiter.length === 0 && (
        <p className="text-slate-600">Noch hat sich niemand angemeldet.</p>
      )}

      {daten && daten.mitarbeiter.length > 0 && (
        <>
          <Raster daten={daten} auswahl={auswahl} waehle={setAuswahl} />
          <Legende daten={daten} />
          {auswahl && <Tagesdetail daten={daten} auswahl={auswahl} />}
          <Leerlauf daten={daten} />
        </>
      )}
    </>
  );
}
